package com.fantasytenis.web.screens.admin

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.bracket.entity.BracketChallengeEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketChallengeStatus
import com.fantasytenis.shared.domain.bracket.entity.BracketEntryEntity
import com.fantasytenis.shared.domain.bracket.entity.LockOverride
import com.fantasytenis.shared.domain.contests.entity.ContestEntity
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.survivor.entity.SurvivorContestEntity
import com.fantasytenis.shared.domain.survivor.entity.SurvivorContestStatus
import com.fantasytenis.shared.domain.survivor.entity.SurvivorEntryEntity
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.ThemeState
import kotlinx.coroutines.launch
import com.fantasytenis.web.theme.ThemeColors
import kotlin.js.Date
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AdminScreen() {
    val currentUser by SharedData.currentUser.collectAsState()
    val showPaidGames by SharedData.showPaidGames.collectAsState()
    val scope = rememberCoroutineScope()
    val colors = Theme.colors
    val isDark by ThemeState.isDarkMode

    var isUpdating by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    // Bracket Challenge Lock Override state (SuperAdmin only)
    var bracketChallenges by remember { mutableStateOf<List<BracketChallengeEntity>>(emptyList()) }
    var isLoadingChallenges by remember { mutableStateOf(false) }
    var overrideError by remember { mutableStateOf<String?>(null) }
    var overrideInProgress by remember { mutableStateOf<String?>(null) } // contestId being modified
    var showReasonDialog by remember { mutableStateOf<Triple<String, LockOverride, String>?>(null) } // (contestId, newOverride, contestType)
    var reasonInput by remember { mutableStateOf("") }

    // Daily Fantasy Contest Lock Override state (SuperAdmin only)
    var dailyContests by remember { mutableStateOf<List<ContestEntity>>(emptyList()) }
    var isLoadingDailyContests by remember { mutableStateOf(false) }

    // Survivor Contest Lock Override state (SuperAdmin only)
    var survivorContests by remember { mutableStateOf<List<SurvivorContestEntity>>(emptyList()) }
    var isLoadingSurvivorContests by remember { mutableStateOf(false) }

    // Entry management state (SuperAdmin only)
    var expandedContestId by remember { mutableStateOf<String?>(null) }
    var expandedContestType by remember { mutableStateOf<String?>(null) } // "contest", "bracket", "survivor"
    var contestEntries by remember { mutableStateOf<List<EntryEntity>>(emptyList()) }
    var bracketEntries by remember { mutableStateOf<List<BracketEntryEntity>>(emptyList()) }
    var survivorEntries by remember { mutableStateOf<List<SurvivorEntryEntity>>(emptyList()) }
    var isLoadingEntries by remember { mutableStateOf(false) }
    var entriesError by remember { mutableStateOf<String?>(null) }
    // Delete entry dialog state: (entryId, entryType, displayName)
    var showDeleteEntryDialog by remember { mutableStateOf<Triple<String, String, String>?>(null) }
    var deleteEntryReason by remember { mutableStateOf("") }
    var deleteInProgress by remember { mutableStateOf(false) }

    // Check if user is admin
    val isAdmin = currentUser?.isAdmin == true
    val isSuperAdmin = currentUser?.isSuperAdmin == true

    // Redirect non-admins
    LaunchedEffect(currentUser) {
        if (currentUser != null && !isAdmin) {
            NavigationState.navigate(Route.Home)
        }
    }

    // Load all contest types for SuperAdmin
    LaunchedEffect(isSuperAdmin) {
        if (isSuperAdmin) {
            // Load bracket challenges
            isLoadingChallenges = true
            DependencyContainer.listBracketChallengesUseCase(
                status = null, // Get all
                featuredOnly = false,
                limit = 50,
                offset = 0
            ).fold(
                onSuccess = { challenges ->
                    bracketChallenges = challenges.filter {
                        it.status == BracketChallengeStatus.UPCOMING ||
                        it.status == BracketChallengeStatus.LIVE
                    }
                    isLoadingChallenges = false
                },
                onFailure = {
                    isLoadingChallenges = false
                }
            )

            // Load daily fantasy contests
            isLoadingDailyContests = true
            DependencyContainer.listContestsUseCase(
                status = null,
                type = null,
                slateId = null,
                limit = 50,
                offset = 0
            ).fold(
                onSuccess = { contests ->
                    dailyContests = contests.filter {
                        it.status == ContestStatus.UPCOMING ||
                        it.status == ContestStatus.LIVE
                    }
                    isLoadingDailyContests = false
                },
                onFailure = {
                    isLoadingDailyContests = false
                }
            )

            // Load survivor contests
            isLoadingSurvivorContests = true
            DependencyContainer.listSurvivorContestsUseCase(
                status = null,
                tournamentId = null,
                featuredOnly = false,
                limit = 50,
                offset = 0
            ).fold(
                onSuccess = { contests ->
                    survivorContests = contests.filter {
                        it.status == SurvivorContestStatus.UPCOMING ||
                        it.status == SurvivorContestStatus.ACTIVE
                    }
                    isLoadingSurvivorContests = false
                },
                onFailure = {
                    isLoadingSurvivorContests = false
                }
            )
        }
    }

    // Helper function to load entries for a contest
    fun loadEntriesForContest(contestId: String, contestType: String) {
        if (expandedContestId == contestId && expandedContestType == contestType) {
            // Collapse if already expanded
            expandedContestId = null
            expandedContestType = null
            return
        }

        expandedContestId = contestId
        expandedContestType = contestType
        isLoadingEntries = true
        entriesError = null

        scope.launch {
            when (contestType) {
                "contest" -> {
                    DependencyContainer.getContestLeaderboardUseCase(
                        contestId = contestId,
                        limit = 100,
                        offset = 0
                    ).fold(
                        onSuccess = { entries ->
                            contestEntries = entries
                            isLoadingEntries = false
                        },
                        onFailure = { e ->
                            entriesError = e.message ?: "Failed to load entries"
                            isLoadingEntries = false
                        }
                    )
                }
                "bracket" -> {
                    DependencyContainer.getBracketLeaderboardUseCase(
                        challengeId = contestId,
                        limit = 100,
                        offset = 0
                    ).fold(
                        onSuccess = { entries ->
                            bracketEntries = entries
                            isLoadingEntries = false
                        },
                        onFailure = { e ->
                            entriesError = e.message ?: "Failed to load entries"
                            isLoadingEntries = false
                        }
                    )
                }
                "survivor" -> {
                    DependencyContainer.getSurvivorLeaderboardUseCase(
                        contestId = contestId,
                        statusFilter = null,
                        limit = 100,
                        offset = 0
                    ).fold(
                        onSuccess = { result ->
                            survivorEntries = result.entries
                            isLoadingEntries = false
                        },
                        onFailure = { e ->
                            entriesError = e.message ?: "Failed to load entries"
                            isLoadingEntries = false
                        }
                    )
                }
            }
        }
    }

    // Helper function to delete an entry
    fun deleteEntry(entryId: String, entryType: String, reason: String) {
        deleteInProgress = true
        scope.launch {
            val result = when (entryType) {
                "contest" -> DependencyContainer.adminDeleteContestEntryUseCase(entryId, reason)
                "bracket" -> DependencyContainer.adminDeleteBracketEntryUseCase(entryId, reason)
                "survivor" -> DependencyContainer.adminDeleteSurvivorEntryUseCase(entryId, reason)
                else -> Result.failure(Exception("Unknown entry type"))
            }

            result.fold(
                onSuccess = {
                    // Remove entry from local list
                    when (entryType) {
                        "contest" -> contestEntries = contestEntries.filter { it.id != entryId }
                        "bracket" -> bracketEntries = bracketEntries.filter { it.id != entryId }
                        "survivor" -> survivorEntries = survivorEntries.filter { it.id != entryId }
                    }
                    showDeleteEntryDialog = null
                    deleteEntryReason = ""
                    deleteInProgress = false
                },
                onFailure = { e ->
                    entriesError = e.message ?: "Failed to delete entry"
                    deleteInProgress = false
                }
            )
        }
    }

    // Show loading if we don't know the user yet
    if (currentUser == null) {
        Div({
            style {
                property("height", "var(--app-height)")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                backgroundColor(colors.background)
            }
        }) {
            Span({
                style {
                    color(colors.textSecondary)
                }
            }) {
                Text("Loading...")
            }
        }
        return
    }

    // Show access denied for non-admins (before redirect kicks in)
    if (!isAdmin) {
        Div({
            style {
                property("height", "var(--app-height)")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                backgroundColor(colors.background)
            }
        }) {
            Span({
                style {
                    color(Colors.Error)
                    fontSize(18.px)
                }
            }) {
                Text("Access Denied")
            }
        }
        return
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = "Admin Panel",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.Home) },
            customActions = {
                // Role badge (visible on desktop)
                Span({
                    style {
                        backgroundColor(rgba(255, 255, 255, 0.15))
                        padding(6.px, 12.px)
                        borderRadius(16.px)
                        color(Colors.White)
                        fontSize(12.px)
                        fontWeight(600)
                    }
                }) {
                    Text(currentUser?.role?.name ?: "Unknown")
                }
            }
        )

        // Main Content
        Main({
            style {
                flex(1)
                overflowY("auto")
                padding(24.px)
            }
        }) {
            Div({
                style {
                    maxWidth(800.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
                // Platform Settings Section
                Section({
                    style {
                        backgroundColor(colors.surface)
                        borderRadius(12.px)
                        padding(24.px)
                        marginBottom(24.px)
                        property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
                    }
                }) {
                    H3({
                        style {
                            fontSize(18.px)
                            fontWeight(600)
                            color(colors.textPrimary)
                            marginBottom(20.px)
                        }
                    }) {
                        Text("Platform Settings")
                    }

                    // Show error if any
                    error?.let { errorMsg ->
                        Div({
                            style {
                                backgroundColor(rgba(244, 67, 54, 0.1))
                                color(Colors.Error)
                                padding(12.px, 16.px)
                                borderRadius(8.px)
                                marginBottom(16.px)
                            }
                        }) {
                            Text(errorMsg)
                        }
                    }

                    // Show Paid Games Toggle
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                            padding(16.px)
                            backgroundColor(if (isDark) Color("#2a2a2a") else Color("#f5f5f5"))
                            borderRadius(8.px)
                        }
                    }) {
                        Div {
                            Div({
                                style {
                                    fontSize(15.px)
                                    fontWeight(500)
                                    color(colors.textPrimary)
                                    marginBottom(4.px)
                                }
                            }) {
                                Text("Show Paid Games")
                            }
                            Div({
                                style {
                                    fontSize(13.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("When disabled, only free contests are shown to users")
                            }
                        }

                        // Toggle switch
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(12.px)
                            }
                        }) {
                            // Current status indicator
                            Span({
                                style {
                                    fontSize(13.px)
                                    color(if (showPaidGames) Colors.Success else colors.textSecondary)
                                    fontWeight(500)
                                }
                            }) {
                                Text(if (showPaidGames) "Enabled" else "Disabled")
                            }

                            // Toggle button
                            Div({
                                style {
                                    width(50.px)
                                    height(26.px)
                                    backgroundColor(if (showPaidGames) colors.primary else Color("#ccc"))
                                    borderRadius(13.px)
                                    cursor(if (isUpdating) "wait" else "pointer")
                                    position(Position.Relative)
                                    property("transition", "background-color 0.2s")
                                    opacity(if (isUpdating) 0.6 else 1.0)
                                }
                                onClick {
                                    if (!isUpdating) {
                                        isUpdating = true
                                        error = null
                                        scope.launch {
                                            DependencyContainer.updatePlatformConfigUseCase(!showPaidGames).fold(
                                                onSuccess = {
                                                    isUpdating = false
                                                },
                                                onFailure = { e ->
                                                    error = e.message ?: "Failed to update setting"
                                                    isUpdating = false
                                                }
                                            )
                                        }
                                    }
                                }
                            }) {
                                // Toggle knob
                                Div({
                                    style {
                                        position(Position.Absolute)
                                        top(3.px)
                                        left(if (showPaidGames) 27.px else 3.px)
                                        width(20.px)
                                        height(20.px)
                                        backgroundColor(Colors.White)
                                        borderRadius(50.percent)
                                        property("transition", "left 0.2s")
                                        property("box-shadow", "0 1px 2px rgba(0,0,0,0.2)")
                                    }
                                })
                            }
                        }
                    }
                }

                // Admin Info Section
                Section({
                    style {
                        backgroundColor(colors.surface)
                        borderRadius(12.px)
                        padding(24.px)
                        property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
                    }
                }) {
                    H3({
                        style {
                            fontSize(18.px)
                            fontWeight(600)
                            color(colors.textPrimary)
                            marginBottom(20.px)
                        }
                    }) {
                        Text("Admin Info")
                    }

                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "auto 1fr")
                            gap(12.px, 24.px)
                        }
                    }) {
                        Span({
                            style {
                                fontSize(13.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text("Email:")
                        }
                        Span({
                            style {
                                fontSize(14.px)
                                color(colors.textPrimary)
                            }
                        }) {
                            Text(currentUser?.email ?: "")
                        }

                        Span({
                            style {
                                fontSize(13.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text("Role:")
                        }
                        Span({
                            style {
                                fontSize(14.px)
                                color(colors.textPrimary)
                                fontWeight(600)
                            }
                        }) {
                            Text(currentUser?.role?.name ?: "")
                        }

                        Span({
                            style {
                                fontSize(13.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text("Name:")
                        }
                        Span({
                            style {
                                fontSize(14.px)
                                color(colors.textPrimary)
                            }
                        }) {
                            Text(currentUser?.fullName ?: "")
                        }
                    }
                }

                // Bracket Challenge Lock Override Section (SuperAdmin only)
                if (isSuperAdmin) {
                    Section({
                        style {
                            backgroundColor(colors.surface)
                            borderRadius(12.px)
                            padding(24.px)
                            marginTop(24.px)
                            property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
                        }
                    }) {
                        H3({
                            style {
                                fontSize(18.px)
                                fontWeight(600)
                                color(colors.textPrimary)
                                marginBottom(20.px)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                            }
                        }) {
                            Text("Bracket Challenge Lock Overrides")
                        }

                        // Show error if any
                        overrideError?.let { errorMsg ->
                            Div({
                                style {
                                    backgroundColor(rgba(244, 67, 54, 0.1))
                                    color(Colors.Error)
                                    padding(12.px, 16.px)
                                    borderRadius(8.px)
                                    marginBottom(16.px)
                                }
                            }) {
                                Text(errorMsg)
                            }
                        }

                        if (isLoadingChallenges) {
                            Div({
                                style {
                                    textAlign("center")
                                    padding(20.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("Loading bracket challenges...")
                            }
                        } else if (bracketChallenges.isEmpty()) {
                            Div({
                                style {
                                    textAlign("center")
                                    padding(20.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("No active bracket challenges")
                            }
                        } else {
                            // Challenge list
                            bracketChallenges.forEach { challenge ->
                                BracketChallengeOverrideCard(
                                    challenge = challenge,
                                    isUpdating = overrideInProgress == challenge.id,
                                    isExpanded = expandedContestId == challenge.id && expandedContestType == "bracket",
                                    colors = colors,
                                    isDark = isDark,
                                    onUnlock = {
                                        showReasonDialog = Triple(challenge.id, LockOverride.FORCE_UNLOCKED, "bracket")
                                        reasonInput = ""
                                    },
                                    onLock = {
                                        showReasonDialog = Triple(challenge.id, LockOverride.FORCE_LOCKED, "bracket")
                                        reasonInput = ""
                                    },
                                    onReset = {
                                        // Reset doesn't need a reason
                                        overrideInProgress = challenge.id
                                        overrideError = null
                                        scope.launch {
                                            DependencyContainer.setLockOverrideUseCase(
                                                challengeId = challenge.id,
                                                lockOverride = LockOverride.NONE,
                                                reason = null
                                            ).fold(
                                                onSuccess = { updatedChallenge ->
                                                    bracketChallenges = bracketChallenges.map {
                                                        if (it.id == challenge.id) updatedChallenge else it
                                                    }
                                                    overrideInProgress = null
                                                },
                                                onFailure = { e ->
                                                    overrideError = e.message ?: "Failed to reset lock override"
                                                    overrideInProgress = null
                                                }
                                            )
                                        }
                                    },
                                    onViewEntries = { loadEntriesForContest(challenge.id, "bracket") }
                                )

                                // Show entries list when expanded
                                if (expandedContestId == challenge.id && expandedContestType == "bracket") {
                                    EntriesList(
                                        contestType = "bracket",
                                        bracketEntries = bracketEntries,
                                        contestEntries = emptyList(),
                                        survivorEntries = emptyList(),
                                        isLoading = isLoadingEntries,
                                        error = entriesError,
                                        colors = colors,
                                        isDark = isDark,
                                        onDeleteEntry = { entryId, displayName ->
                                            showDeleteEntryDialog = Triple(entryId, "bracket", displayName)
                                            deleteEntryReason = ""
                                        }
                                    )
                                }
                            }
                        }
                    }

                    // Daily Fantasy Lock Override Section
                    Section({
                        style {
                            backgroundColor(colors.surface)
                            borderRadius(12.px)
                            padding(24.px)
                            marginTop(24.px)
                            property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
                        }
                    }) {
                        H3({
                            style {
                                fontSize(18.px)
                                fontWeight(600)
                                color(colors.textPrimary)
                                marginBottom(20.px)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                            }
                        }) {
                            Text("Daily Fantasy Lock Overrides")
                        }

                        if (isLoadingDailyContests) {
                            Div({
                                style {
                                    textAlign("center")
                                    padding(20.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("Loading daily fantasy contests...")
                            }
                        } else if (dailyContests.isEmpty()) {
                            Div({
                                style {
                                    textAlign("center")
                                    padding(20.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("No active daily fantasy contests")
                            }
                        } else {
                            dailyContests.forEach { contest ->
                                ContestOverrideCard(
                                    contest = contest,
                                    isUpdating = overrideInProgress == contest.id,
                                    isExpanded = expandedContestId == contest.id && expandedContestType == "contest",
                                    colors = colors,
                                    isDark = isDark,
                                    onUnlock = {
                                        showReasonDialog = Triple(contest.id, LockOverride.FORCE_UNLOCKED, "contest")
                                        reasonInput = ""
                                    },
                                    onLock = {
                                        showReasonDialog = Triple(contest.id, LockOverride.FORCE_LOCKED, "contest")
                                        reasonInput = ""
                                    },
                                    onReset = {
                                        overrideInProgress = contest.id
                                        overrideError = null
                                        scope.launch {
                                            DependencyContainer.setContestLockOverrideUseCase(
                                                contestId = contest.id,
                                                lockOverride = LockOverride.NONE,
                                                reason = null
                                            ).fold(
                                                onSuccess = { updatedContest ->
                                                    dailyContests = dailyContests.map {
                                                        if (it.id == contest.id) updatedContest else it
                                                    }
                                                    overrideInProgress = null
                                                },
                                                onFailure = { e ->
                                                    overrideError = e.message ?: "Failed to reset lock override"
                                                    overrideInProgress = null
                                                }
                                            )
                                        }
                                    },
                                    onViewEntries = { loadEntriesForContest(contest.id, "contest") }
                                )

                                // Show entries list when expanded
                                if (expandedContestId == contest.id && expandedContestType == "contest") {
                                    EntriesList(
                                        contestType = "contest",
                                        bracketEntries = emptyList(),
                                        contestEntries = contestEntries,
                                        survivorEntries = emptyList(),
                                        isLoading = isLoadingEntries,
                                        error = entriesError,
                                        colors = colors,
                                        isDark = isDark,
                                        onDeleteEntry = { entryId, displayName ->
                                            showDeleteEntryDialog = Triple(entryId, "contest", displayName)
                                            deleteEntryReason = ""
                                        }
                                    )
                                }
                            }
                        }
                    }

                    // Survivor Contest Lock Override Section
                    Section({
                        style {
                            backgroundColor(colors.surface)
                            borderRadius(12.px)
                            padding(24.px)
                            marginTop(24.px)
                            property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
                        }
                    }) {
                        H3({
                            style {
                                fontSize(18.px)
                                fontWeight(600)
                                color(colors.textPrimary)
                                marginBottom(20.px)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                            }
                        }) {
                            Text("Survivor Contest Lock Overrides")
                        }

                        if (isLoadingSurvivorContests) {
                            Div({
                                style {
                                    textAlign("center")
                                    padding(20.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("Loading survivor contests...")
                            }
                        } else if (survivorContests.isEmpty()) {
                            Div({
                                style {
                                    textAlign("center")
                                    padding(20.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("No active survivor contests")
                            }
                        } else {
                            survivorContests.forEach { contest ->
                                SurvivorContestOverrideCard(
                                    contest = contest,
                                    isUpdating = overrideInProgress == contest.id,
                                    isExpanded = expandedContestId == contest.id && expandedContestType == "survivor",
                                    colors = colors,
                                    isDark = isDark,
                                    onUnlock = {
                                        showReasonDialog = Triple(contest.id, LockOverride.FORCE_UNLOCKED, "survivor")
                                        reasonInput = ""
                                    },
                                    onLock = {
                                        showReasonDialog = Triple(contest.id, LockOverride.FORCE_LOCKED, "survivor")
                                        reasonInput = ""
                                    },
                                    onReset = {
                                        overrideInProgress = contest.id
                                        overrideError = null
                                        scope.launch {
                                            DependencyContainer.setSurvivorLockOverrideUseCase(
                                                contestId = contest.id,
                                                lockOverride = LockOverride.NONE,
                                                reason = null
                                            ).fold(
                                                onSuccess = { updatedContest ->
                                                    survivorContests = survivorContests.map {
                                                        if (it.id == contest.id) updatedContest else it
                                                    }
                                                    overrideInProgress = null
                                                },
                                                onFailure = { e ->
                                                    overrideError = e.message ?: "Failed to reset lock override"
                                                    overrideInProgress = null
                                                }
                                            )
                                        }
                                    },
                                    onViewEntries = { loadEntriesForContest(contest.id, "survivor") }
                                )

                                // Show entries list when expanded
                                if (expandedContestId == contest.id && expandedContestType == "survivor") {
                                    EntriesList(
                                        contestType = "survivor",
                                        bracketEntries = emptyList(),
                                        contestEntries = emptyList(),
                                        survivorEntries = survivorEntries,
                                        isLoading = isLoadingEntries,
                                        error = entriesError,
                                        colors = colors,
                                        isDark = isDark,
                                        onDeleteEntry = { entryId, displayName ->
                                            showDeleteEntryDialog = Triple(entryId, "survivor", displayName)
                                            deleteEntryReason = ""
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        // Reason Dialog
        showReasonDialog?.let { (contestId, newOverride, contestType) ->
            val dialogTitle = when (contestType) {
                "bracket" -> when (newOverride) {
                    LockOverride.FORCE_UNLOCKED -> "Unlock Bracket Challenge"
                    LockOverride.FORCE_LOCKED -> "Lock Bracket Challenge"
                    LockOverride.NONE -> "Reset Lock Override"
                }
                "contest" -> when (newOverride) {
                    LockOverride.FORCE_UNLOCKED -> "Unlock Daily Fantasy Contest"
                    LockOverride.FORCE_LOCKED -> "Lock Daily Fantasy Contest"
                    LockOverride.NONE -> "Reset Lock Override"
                }
                "survivor" -> when (newOverride) {
                    LockOverride.FORCE_UNLOCKED -> "Unlock Survivor Contest"
                    LockOverride.FORCE_LOCKED -> "Lock Survivor Contest"
                    LockOverride.NONE -> "Reset Lock Override"
                }
                else -> "Lock Override"
            }

            ReasonDialog(
                title = dialogTitle,
                reason = reasonInput,
                onReasonChange = { reasonInput = it },
                colors = colors,
                isDark = isDark,
                onConfirm = {
                    showReasonDialog = null
                    overrideInProgress = contestId
                    overrideError = null
                    scope.launch {
                        when (contestType) {
                            "bracket" -> {
                                DependencyContainer.setLockOverrideUseCase(
                                    challengeId = contestId,
                                    lockOverride = newOverride,
                                    reason = reasonInput.ifBlank { null }
                                ).fold(
                                    onSuccess = { updatedChallenge ->
                                        bracketChallenges = bracketChallenges.map {
                                            if (it.id == contestId) updatedChallenge else it
                                        }
                                        overrideInProgress = null
                                    },
                                    onFailure = { e ->
                                        overrideError = e.message ?: "Failed to update lock override"
                                        overrideInProgress = null
                                    }
                                )
                            }
                            "contest" -> {
                                DependencyContainer.setContestLockOverrideUseCase(
                                    contestId = contestId,
                                    lockOverride = newOverride,
                                    reason = reasonInput.ifBlank { null }
                                ).fold(
                                    onSuccess = { updatedContest ->
                                        dailyContests = dailyContests.map {
                                            if (it.id == contestId) updatedContest else it
                                        }
                                        overrideInProgress = null
                                    },
                                    onFailure = { e ->
                                        overrideError = e.message ?: "Failed to update lock override"
                                        overrideInProgress = null
                                    }
                                )
                            }
                            "survivor" -> {
                                DependencyContainer.setSurvivorLockOverrideUseCase(
                                    contestId = contestId,
                                    lockOverride = newOverride,
                                    reason = reasonInput.ifBlank { null }
                                ).fold(
                                    onSuccess = { updatedContest ->
                                        survivorContests = survivorContests.map {
                                            if (it.id == contestId) updatedContest else it
                                        }
                                        overrideInProgress = null
                                    },
                                    onFailure = { e ->
                                        overrideError = e.message ?: "Failed to update lock override"
                                        overrideInProgress = null
                                    }
                                )
                            }
                        }
                    }
                },
                onCancel = {
                    showReasonDialog = null
                    reasonInput = ""
                }
            )
        }

        // Delete Entry Dialog
        showDeleteEntryDialog?.let { (entryId, entryType, displayName) ->
            val dialogTitle = "Delete Entry"
            val dialogDescription = "Are you sure you want to delete the entry for $displayName? This action cannot be undone."

            DeleteEntryDialog(
                title = dialogTitle,
                description = dialogDescription,
                reason = deleteEntryReason,
                onReasonChange = { deleteEntryReason = it },
                isDeleting = deleteInProgress,
                colors = colors,
                isDark = isDark,
                onConfirm = {
                    if (deleteEntryReason.isNotBlank()) {
                        deleteEntry(entryId, entryType, deleteEntryReason)
                    }
                },
                onCancel = {
                    showDeleteEntryDialog = null
                    deleteEntryReason = ""
                }
            )
        }
    }
}

@Composable
private fun BracketChallengeOverrideCard(
    challenge: BracketChallengeEntity,
    isUpdating: Boolean,
    isExpanded: Boolean,
    colors: ThemeColors,
    isDark: Boolean,
    onUnlock: () -> Unit,
    onLock: () -> Unit,
    onReset: () -> Unit,
    onViewEntries: () -> Unit
) {
    Div({
        style {
            backgroundColor(if (isDark) Color("#2a2a2a") else Color("#f5f5f5"))
            borderRadius(8.px)
            padding(16.px)
            marginBottom(12.px)
            opacity(if (isUpdating) 0.6 else 1.0)
        }
    }) {
        // Challenge Info
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            Div {
                Div({
                    style {
                        fontSize(15.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                        marginBottom(4.px)
                    }
                }) {
                    Text(challenge.name)
                }
                Div({
                    style {
                        fontSize(13.px)
                        color(colors.textSecondary)
                        marginBottom(4.px)
                    }
                }) {
                    Text(challenge.tournamentName)
                }
                Div({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("Lock time: ${formatLockTime(challenge.lockTime)}")
                }
            }

            // Status badge
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.FlexEnd)
                    gap(4.px)
                }
            }) {
                // Override status badge
                when (challenge.lockOverride) {
                    LockOverride.FORCE_UNLOCKED -> {
                        Span({
                            style {
                                backgroundColor(Colors.Success)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("MANUALLY UNLOCKED")
                        }
                    }
                    LockOverride.FORCE_LOCKED -> {
                        Span({
                            style {
                                backgroundColor(Colors.Error)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("MANUALLY LOCKED")
                        }
                    }
                    LockOverride.NONE -> {
                        Span({
                            style {
                                backgroundColor(colors.textSecondary)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("TIME-BASED")
                        }
                    }
                }
                // Current lock state
                Span({
                    style {
                        fontSize(12.px)
                        color(if (challenge.isLocked) Colors.Error else Colors.Success)
                    }
                }) {
                    Text(if (challenge.isLocked) "Currently: Locked" else "Currently: Unlocked")
                }
            }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            // Unlock button - show when not already force unlocked
            if (challenge.lockOverride != LockOverride.FORCE_UNLOCKED) {
                Div({
                    style {
                        backgroundColor(Colors.Success)
                        color(Colors.White)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                    }
                    onClick { if (!isUpdating) onUnlock() }
                }) {
                    Text("Unlock")
                }
            }

            // Lock button - show when not already force locked
            if (challenge.lockOverride != LockOverride.FORCE_LOCKED) {
                Div({
                    style {
                        backgroundColor(Colors.Warning)
                        color(Colors.White)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                    }
                    onClick { if (!isUpdating) onLock() }
                }) {
                    Text("Lock")
                }
            }

            // Reset button - only show when there's an override
            if (challenge.hasLockOverride) {
                Div({
                    style {
                        backgroundColor(Color("transparent"))
                        color(colors.textSecondary)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                        border(1.px, LineStyle.Solid, colors.textSecondary)
                    }
                    onClick { if (!isUpdating) onReset() }
                }) {
                    Text("Reset to Time-Based")
                }
            }

            // View Entries button
            Div({
                style {
                    backgroundColor(colors.primary)
                    color(Colors.White)
                    padding(8.px, 16.px)
                    borderRadius(6.px)
                    fontSize(13.px)
                    fontWeight(500)
                    cursor("pointer")
                }
                onClick { onViewEntries() }
            }) {
                Text(if (isExpanded) "Hide Entries" else "View Entries")
            }
        }
    }
}

@Composable
private fun ContestOverrideCard(
    contest: ContestEntity,
    isUpdating: Boolean,
    isExpanded: Boolean,
    colors: ThemeColors,
    isDark: Boolean,
    onUnlock: () -> Unit,
    onLock: () -> Unit,
    onReset: () -> Unit,
    onViewEntries: () -> Unit
) {
    Div({
        style {
            backgroundColor(if (isDark) Color("#2a2a2a") else Color("#f5f5f5"))
            borderRadius(8.px)
            padding(16.px)
            marginBottom(12.px)
            opacity(if (isUpdating) 0.6 else 1.0)
        }
    }) {
        // Contest Info
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            Div {
                Div({
                    style {
                        fontSize(15.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                        marginBottom(4.px)
                    }
                }) {
                    Text(contest.name)
                }
                Div({
                    style {
                        fontSize(13.px)
                        color(colors.textSecondary)
                        marginBottom(4.px)
                    }
                }) {
                    Text("Entry: $${contest.entryFee} | Prize: $${contest.prizePool}")
                }
                Div({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("Lock time: ${formatLockTime(contest.lineupLockTime)}")
                }
            }

            // Status badge
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.FlexEnd)
                    gap(4.px)
                }
            }) {
                // Override status badge
                when (contest.lockOverride) {
                    LockOverride.FORCE_UNLOCKED -> {
                        Span({
                            style {
                                backgroundColor(Colors.Success)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("MANUALLY UNLOCKED")
                        }
                    }
                    LockOverride.FORCE_LOCKED -> {
                        Span({
                            style {
                                backgroundColor(Colors.Error)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("MANUALLY LOCKED")
                        }
                    }
                    LockOverride.NONE -> {
                        Span({
                            style {
                                backgroundColor(colors.textSecondary)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("TIME-BASED")
                        }
                    }
                }
                // Current lock state
                Span({
                    style {
                        fontSize(12.px)
                        color(if (contest.isLocked) Colors.Error else Colors.Success)
                    }
                }) {
                    Text(if (contest.isLocked) "Currently: Locked" else "Currently: Unlocked")
                }
            }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            if (contest.lockOverride != LockOverride.FORCE_UNLOCKED) {
                Div({
                    style {
                        backgroundColor(Colors.Success)
                        color(Colors.White)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                    }
                    onClick { if (!isUpdating) onUnlock() }
                }) {
                    Text("Unlock")
                }
            }

            if (contest.lockOverride != LockOverride.FORCE_LOCKED) {
                Div({
                    style {
                        backgroundColor(Colors.Warning)
                        color(Colors.White)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                    }
                    onClick { if (!isUpdating) onLock() }
                }) {
                    Text("Lock")
                }
            }

            if (contest.hasLockOverride) {
                Div({
                    style {
                        backgroundColor(Color("transparent"))
                        color(colors.textSecondary)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                        border(1.px, LineStyle.Solid, colors.textSecondary)
                    }
                    onClick { if (!isUpdating) onReset() }
                }) {
                    Text("Reset to Time-Based")
                }
            }

            // View Entries button
            Div({
                style {
                    backgroundColor(colors.primary)
                    color(Colors.White)
                    padding(8.px, 16.px)
                    borderRadius(6.px)
                    fontSize(13.px)
                    fontWeight(500)
                    cursor("pointer")
                }
                onClick { onViewEntries() }
            }) {
                Text(if (isExpanded) "Hide Entries" else "View Entries")
            }
        }
    }
}

@Composable
private fun SurvivorContestOverrideCard(
    contest: SurvivorContestEntity,
    isUpdating: Boolean,
    isExpanded: Boolean,
    colors: ThemeColors,
    isDark: Boolean,
    onUnlock: () -> Unit,
    onLock: () -> Unit,
    onReset: () -> Unit,
    onViewEntries: () -> Unit
) {
    Div({
        style {
            backgroundColor(if (isDark) Color("#2a2a2a") else Color("#f5f5f5"))
            borderRadius(8.px)
            padding(16.px)
            marginBottom(12.px)
            opacity(if (isUpdating) 0.6 else 1.0)
        }
    }) {
        // Contest Info
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            Div {
                Div({
                    style {
                        fontSize(15.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                        marginBottom(4.px)
                    }
                }) {
                    Text(contest.name)
                }
                Div({
                    style {
                        fontSize(13.px)
                        color(colors.textSecondary)
                        marginBottom(4.px)
                    }
                }) {
                    Text(contest.tournamentName)
                }
                Div({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                    }
                }) {
                    val deadline = contest.roundDeadline ?: "No deadline set"
                    Text("Round deadline: ${formatLockTime(deadline)}")
                }
            }

            // Status badge
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.FlexEnd)
                    gap(4.px)
                }
            }) {
                // Override status badge
                when (contest.lockOverride) {
                    LockOverride.FORCE_UNLOCKED -> {
                        Span({
                            style {
                                backgroundColor(Colors.Success)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("MANUALLY UNLOCKED")
                        }
                    }
                    LockOverride.FORCE_LOCKED -> {
                        Span({
                            style {
                                backgroundColor(Colors.Error)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("MANUALLY LOCKED")
                        }
                    }
                    LockOverride.NONE -> {
                        Span({
                            style {
                                backgroundColor(colors.textSecondary)
                                color(Colors.White)
                                padding(4.px, 8.px)
                                borderRadius(4.px)
                                fontSize(11.px)
                                fontWeight(600)
                            }
                        }) {
                            Text("TIME-BASED")
                        }
                    }
                }
                // Current lock state
                Span({
                    style {
                        fontSize(12.px)
                        color(if (contest.isRoundLocked) Colors.Error else Colors.Success)
                    }
                }) {
                    Text(if (contest.isRoundLocked) "Currently: Locked" else "Currently: Unlocked")
                }
            }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            if (contest.lockOverride != LockOverride.FORCE_UNLOCKED) {
                Div({
                    style {
                        backgroundColor(Colors.Success)
                        color(Colors.White)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                    }
                    onClick { if (!isUpdating) onUnlock() }
                }) {
                    Text("Unlock")
                }
            }

            if (contest.lockOverride != LockOverride.FORCE_LOCKED) {
                Div({
                    style {
                        backgroundColor(Colors.Warning)
                        color(Colors.White)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                    }
                    onClick { if (!isUpdating) onLock() }
                }) {
                    Text("Lock")
                }
            }

            if (contest.hasLockOverride) {
                Div({
                    style {
                        backgroundColor(Color("transparent"))
                        color(colors.textSecondary)
                        padding(8.px, 16.px)
                        borderRadius(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor(if (isUpdating) "wait" else "pointer")
                        border(1.px, LineStyle.Solid, colors.textSecondary)
                    }
                    onClick { if (!isUpdating) onReset() }
                }) {
                    Text("Reset to Time-Based")
                }
            }

            // View Entries button
            Div({
                style {
                    backgroundColor(colors.primary)
                    color(Colors.White)
                    padding(8.px, 16.px)
                    borderRadius(6.px)
                    fontSize(13.px)
                    fontWeight(500)
                    cursor("pointer")
                }
                onClick { onViewEntries() }
            }) {
                Text(if (isExpanded) "Hide Entries" else "View Entries")
            }
        }
    }
}

@Composable
private fun EntriesList(
    contestType: String,
    bracketEntries: List<BracketEntryEntity>,
    contestEntries: List<EntryEntity>,
    survivorEntries: List<SurvivorEntryEntity>,
    isLoading: Boolean,
    error: String?,
    colors: ThemeColors,
    isDark: Boolean,
    onDeleteEntry: (entryId: String, displayName: String) -> Unit
) {
    Div({
        style {
            backgroundColor(if (isDark) Color("#1a1a1a") else Color("#e8e8e8"))
            borderRadius(8.px)
            padding(16.px)
            marginBottom(12.px)
            marginTop((-8).px) // Connect visually with the card above
        }
    }) {
        if (isLoading) {
            Div({
                style {
                    textAlign("center")
                    padding(16.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("Loading entries...")
            }
        } else if (error != null) {
            Div({
                style {
                    backgroundColor(rgba(244, 67, 54, 0.1))
                    color(Colors.Error)
                    padding(12.px, 16.px)
                    borderRadius(8.px)
                }
            }) {
                Text(error)
            }
        } else {
            // Header row
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(12.px)
                    paddingBottom(8.px)
                    property("border-bottom", "1px solid ${if (isDark) "#444" else "#ccc"}")
                }
            }) {
                Span({
                    style {
                        fontSize(14.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                    }
                }) {
                    val count = when (contestType) {
                        "bracket" -> bracketEntries.size
                        "contest" -> contestEntries.size
                        "survivor" -> survivorEntries.size
                        else -> 0
                    }
                    Text("$count Entries")
                }
            }

            // Entry rows
            when (contestType) {
                "bracket" -> {
                    if (bracketEntries.isEmpty()) {
                        Div({
                            style {
                                textAlign("center")
                                padding(16.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text("No entries")
                        }
                    } else {
                        bracketEntries.forEach { entry ->
                            EntryRow(
                                displayName = entry.displayName,
                                status = entry.status.name,
                                points = entry.totalPoints,
                                rank = entry.rank,
                                colors = colors,
                                isDark = isDark,
                                onDelete = { onDeleteEntry(entry.id, entry.displayName) }
                            )
                        }
                    }
                }
                "contest" -> {
                    if (contestEntries.isEmpty()) {
                        Div({
                            style {
                                textAlign("center")
                                padding(16.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text("No entries")
                        }
                    } else {
                        contestEntries.forEach { entry ->
                            EntryRow(
                                displayName = entry.formattedDisplayName,
                                status = entry.status.name,
                                points = entry.totalPoints,
                                rank = entry.rank,
                                colors = colors,
                                isDark = isDark,
                                onDelete = { onDeleteEntry(entry.id, entry.formattedDisplayName) }
                            )
                        }
                    }
                }
                "survivor" -> {
                    if (survivorEntries.isEmpty()) {
                        Div({
                            style {
                                textAlign("center")
                                padding(16.px)
                                color(colors.textSecondary)
                            }
                        }) {
                            Text("No entries")
                        }
                    } else {
                        survivorEntries.forEach { entry ->
                            EntryRow(
                                displayName = entry.displayName,
                                status = entry.status.displayName,
                                points = entry.survivedRounds.toDouble(),
                                rank = entry.rank,
                                colors = colors,
                                isDark = isDark,
                                onDelete = { onDeleteEntry(entry.id, entry.displayName) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EntryRow(
    displayName: String,
    status: String,
    points: Double,
    rank: Int?,
    colors: ThemeColors,
    isDark: Boolean,
    onDelete: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(8.px, 4.px)
            borderRadius(4.px)
            marginBottom(4.px)
            backgroundColor(if (isDark) Color("#2a2a2a") else Color("#f5f5f5"))
        }
    }) {
        // User info
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                flex(1)
            }
        }) {
            // Rank
            if (rank != null) {
                Span({
                    style {
                        width(30.px)
                        fontSize(13.px)
                        fontWeight(600)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("#$rank")
                }
            }

            // Name
            Span({
                style {
                    fontSize(14.px)
                    color(colors.textPrimary)
                    fontWeight(500)
                }
            }) {
                Text(displayName)
            }

            // Status badge
            Span({
                style {
                    fontSize(11.px)
                    padding(2.px, 6.px)
                    borderRadius(4.px)
                    backgroundColor(if (isDark) Color("#444") else Color("#ddd"))
                    color(colors.textSecondary)
                }
            }) {
                Text(status)
            }
        }

        // Points and delete button
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            // Points
            Span({
                style {
                    fontSize(13.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("${points.toInt()} pts")
            }

            // Delete button
            Div({
                style {
                    backgroundColor(Colors.Error)
                    color(Colors.White)
                    padding(4.px, 10.px)
                    borderRadius(4.px)
                    fontSize(12.px)
                    fontWeight(500)
                    cursor("pointer")
                }
                onClick { onDelete() }
            }) {
                Text("Delete")
            }
        }
    }
}

@Composable
private fun DeleteEntryDialog(
    title: String,
    description: String,
    reason: String,
    onReasonChange: (String) -> Unit,
    isDeleting: Boolean,
    colors: ThemeColors,
    isDark: Boolean,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    // Overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.5))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
        }
        onClick { if (!isDeleting) onCancel() }
    }) {
        // Dialog box
        Div({
            style {
                backgroundColor(colors.surface)
                borderRadius(12.px)
                padding(24.px)
                width(400.px)
                maxWidth(90.percent)
                property("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            H4({
                style {
                    fontSize(18.px)
                    fontWeight(600)
                    color(colors.textPrimary)
                    marginBottom(8.px)
                }
            }) {
                Text(title)
            }

            Div({
                style {
                    fontSize(14.px)
                    color(colors.textSecondary)
                    marginBottom(16.px)
                }
            }) {
                Text(description)
            }

            Div({
                style {
                    marginBottom(16.px)
                }
            }) {
                Label(attrs = {
                    style {
                        fontSize(13.px)
                        color(colors.textSecondary)
                        display(DisplayStyle.Block)
                        marginBottom(8.px)
                    }
                }) {
                    Text("Reason (required)")
                }
                TextArea(reason, {
                    style {
                        width(100.percent)
                        minHeight(80.px)
                        padding(12.px)
                        borderRadius(8.px)
                        border(1.px, LineStyle.Solid, if (isDark) Color("#444") else Color("#ddd"))
                        backgroundColor(if (isDark) Color("#2a2a2a") else Color("#f9f9f9"))
                        color(colors.textPrimary)
                        fontSize(14.px)
                        property("resize", "vertical")
                    }
                    onInput { e ->
                        onReasonChange(e.value)
                    }
                })
            }

            // Buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(12.px)
                }
            }) {
                Div({
                    style {
                        padding(10.px, 20.px)
                        borderRadius(6.px)
                        fontSize(14.px)
                        cursor(if (isDeleting) "wait" else "pointer")
                        color(colors.textSecondary)
                        opacity(if (isDeleting) 0.5 else 1.0)
                    }
                    onClick { if (!isDeleting) onCancel() }
                }) {
                    Text("Cancel")
                }
                Div({
                    style {
                        backgroundColor(if (reason.isBlank()) Color("#ccc") else Colors.Error)
                        color(Colors.White)
                        padding(10.px, 20.px)
                        borderRadius(6.px)
                        fontSize(14.px)
                        fontWeight(500)
                        cursor(if (isDeleting || reason.isBlank()) "not-allowed" else "pointer")
                        opacity(if (isDeleting) 0.5 else 1.0)
                    }
                    onClick { if (!isDeleting && reason.isNotBlank()) onConfirm() }
                }) {
                    Text(if (isDeleting) "Deleting..." else "Delete Entry")
                }
            }
        }
    }
}

@Composable
private fun ReasonDialog(
    title: String,
    reason: String,
    onReasonChange: (String) -> Unit,
    colors: ThemeColors,
    isDark: Boolean,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    // Overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.5))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
        }
        onClick { onCancel() }
    }) {
        // Dialog box
        Div({
            style {
                backgroundColor(colors.surface)
                borderRadius(12.px)
                padding(24.px)
                width(400.px)
                maxWidth(90.percent)
                property("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            H4({
                style {
                    fontSize(18.px)
                    fontWeight(600)
                    color(colors.textPrimary)
                    marginBottom(16.px)
                }
            }) {
                Text(title)
            }

            Div({
                style {
                    marginBottom(16.px)
                }
            }) {
                Label(attrs = {
                    style {
                        fontSize(13.px)
                        color(colors.textSecondary)
                        display(DisplayStyle.Block)
                        marginBottom(8.px)
                    }
                }) {
                    Text("Reason (optional)")
                }
                TextArea(reason, {
                    style {
                        width(100.percent)
                        minHeight(80.px)
                        padding(12.px)
                        borderRadius(8.px)
                        border(1.px, LineStyle.Solid, if (isDark) Color("#444") else Color("#ddd"))
                        backgroundColor(if (isDark) Color("#2a2a2a") else Color("#f9f9f9"))
                        color(colors.textPrimary)
                        fontSize(14.px)
                        property("resize", "vertical")
                    }
                    onInput { e ->
                        onReasonChange(e.value)
                    }
                })
            }

            // Buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(12.px)
                }
            }) {
                Div({
                    style {
                        padding(10.px, 20.px)
                        borderRadius(6.px)
                        fontSize(14.px)
                        cursor("pointer")
                        color(colors.textSecondary)
                    }
                    onClick { onCancel() }
                }) {
                    Text("Cancel")
                }
                Div({
                    style {
                        backgroundColor(colors.primary)
                        color(Colors.White)
                        padding(10.px, 20.px)
                        borderRadius(6.px)
                        fontSize(14.px)
                        fontWeight(500)
                        cursor("pointer")
                    }
                    onClick { onConfirm() }
                }) {
                    Text("Confirm")
                }
            }
        }
    }
}

private fun formatLockTime(lockTime: String): String {
    return try {
        val date = Date(lockTime)
        val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        val month = months[date.getMonth()]
        val day = date.getDate()
        val hour = date.getHours()
        val minute = date.getMinutes().toString().padStart(2, '0')
        val amPm = if (hour < 12) "AM" else "PM"
        val hour12 = if (hour == 0) 12 else if (hour > 12) hour - 12 else hour
        "$month $day at $hour12:$minute $amPm"
    } catch (e: Exception) {
        lockTime
    }
}

private fun rgba(r: Int, g: Int, b: Int, a: Double): CSSColorValue {
    return Color("rgba($r, $g, $b, $a)")
}
